import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminStats, adminArticles, adminBlocked, adminVideos, adminSubscribers, adminSponsors, adminRefresh, adminHeaders } from "../lib/api";
import axios from "axios";
import { Loader2, ShieldAlert, RefreshCcw, Database, Users, Megaphone, BarChart3, Video, Newspaper, MessageSquare, TrendingUp } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LogoMark = () => (
  <svg viewBox="0 0 64 64" fill="none" className="w-[26px] h-[26px]">
    <path d="M12 34c14-18 25-18 40 0" stroke="white" strokeWidth="7" strokeLinecap="round" />
    <path d="M16 45h32" stroke="white" strokeWidth="7" strokeLinecap="round" />
    <circle cx="32" cy="24" r="6" fill="white" />
  </svg>
);

const scoreCls = (key, val) => {
  if (key === "noise") return val >= 60 ? "text-[var(--red)]" : val >= 35 ? "text-[var(--gold)]" : "text-[var(--green)]";
  return val >= 75 ? "text-[var(--green)]" : val >= 50 ? "text-[var(--gold)]" : "text-[var(--red)]";
};

export default function Admin() {
  const [key, setKey] = useState(localStorage.getItem("newseze.admin") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("articles");
  const [stats, setStats] = useState(null);
  const [articles, setArticles] = useState([]);
  const [blocked, setBlocked] = useState([]);
  const [videos, setVideos] = useState([]);
  const [subs, setSubs] = useState([]);
  const [sponsors, setSponsors] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editorial, setEditorial] = useState(null);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState("");

  const load = async (k = key) => {
    setErr("");
    try {
      const [s, a, b, v, su, sp, an, cm, ed] = await Promise.all([
        adminStats(k), adminArticles(k, { limit: 60 }), adminBlocked(k), adminVideos(k), adminSubscribers(k), adminSponsors(k),
        axios.get(`${API}/admin/analytics`, adminHeaders(k)).then((r) => r.data).catch(() => null),
        axios.get(`${API}/admin/contact-messages`, adminHeaders(k)).then((r) => r.data).catch(() => []),
        axios.get(`${API}/admin/editorial-analytics`, adminHeaders(k)).then((r) => r.data).catch(() => null),
      ]);
      setStats(s); setArticles(a); setBlocked(b); setVideos(v); setSubs(su); setSponsors(sp);
      setAnalytics(an); setContactMsgs(cm); setEditorial(ed);
      setAuthed(true);
      localStorage.setItem("newseze.admin", k);
    } catch (e) {
      setErr(e?.response?.status === 401 ? "Wrong admin key." : "Could not load admin data.");
      setAuthed(false);
    }
  };

  useEffect(() => { if (key) load(key); /* try existing */ }, []); // eslint-disable-line

  const onSubmitKey = (e) => { e.preventDefault(); load(key); };

  const onRefresh = async () => {
    setRefreshing(true);
    try { await adminRefresh(key); setTimeout(() => load(), 2000); } catch (e) { console.error(e); }
    setTimeout(() => setRefreshing(false), 4000);
  };

  if (!authed) {
    return (
      <main className="min-h-screen grid place-items-center bg-[var(--bg)] p-5">
        <form onSubmit={onSubmitKey} className="w-full max-w-[400px] bg-white border border-[var(--line)] rounded-2xl p-7" data-testid="admin-login">
          <div className="flex items-center gap-3 mb-4">
            <span className="logo"><LogoMark /></span>
            <div>
              <div className="text-[22px] font-black tracking-[-0.03em]">Newseze Operator</div>
              <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--muted)] font-bold">Internal · Not for public</div>
            </div>
          </div>
          <p className="text-[13px] text-[var(--muted)] mb-3">
            Enter your admin key to view the signal engine, blocked stories, subscribers, and sponsor inquiries.
          </p>
          <input
            type="password"
            className="field"
            placeholder="Admin key"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            data-testid="admin-key-input"
          />
          {err && <div className="text-[var(--red)] text-[13px] mt-2">{err}</div>}
          <button className="btn primary w-full justify-center mt-3" type="submit" data-testid="admin-login-btn">Enter</button>
          <Link to="/" className="block text-center text-[12px] text-[var(--muted)] hover:text-[var(--blue)] mt-3">
            ← Back to public site
          </Link>
        </form>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[var(--bg)]">
      <header className="bg-[var(--ink)] text-white">
        <div className="max-w-[1380px] mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3 text-white">
            <span className="logo"><LogoMark /></span>
            <div>
              <div className="text-[18px] font-black tracking-[-0.03em]">Newseze Operator</div>
              <div className="text-[10px] tracking-[0.16em] uppercase opacity-70">Internal · Signal Engine</div>
            </div>
          </Link>
          <div className="flex items-center gap-2">
            <button onClick={onRefresh} disabled={refreshing} className="btn ghost" data-testid="admin-refresh-btn">
              {refreshing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCcw size={14} />} Refresh wire
            </button>
            <Link to="/" className="btn ghost">Exit</Link>
          </div>
        </div>
      </header>

      <section className="max-w-[1380px] mx-auto px-5 py-6 grid grid-cols-2 md:grid-cols-6 gap-3">
        {[
          { k: "articles", icon: Newspaper, label: "Articles", val: stats?.articles ?? 0 },
          { k: "blocked", icon: ShieldAlert, label: "Blocked", val: stats?.articles_blocked ?? 0 },
          { k: "videos", icon: Video, label: "Videos", val: stats?.videos ?? 0 },
          { k: "podcasts", icon: Megaphone, label: "Podcasts", val: stats?.podcasts ?? 0 },
          { k: "subscribers", icon: Users, label: "Subscribers", val: stats?.subscribers ?? 0 },
          { k: "sponsors", icon: BarChart3, label: "Sponsor Leads", val: stats?.sponsor_inquiries ?? 0 },
        ].map((s) => (
          <div key={s.k} className="bg-white border border-[var(--line)] rounded-md p-3" data-testid={`stat-${s.k}`}>
            <s.icon size={14} className="text-[var(--blue)]" />
            <div className="text-[26px] font-black tracking-[-0.02em] text-[var(--ink)] mt-1">{s.val}</div>
            <div className="text-[10px] uppercase tracking-[0.12em] text-[var(--muted)] font-bold">{s.label}</div>
          </div>
        ))}
      </section>

      <section className="max-w-[1380px] mx-auto px-5">
        <div className="flex flex-wrap gap-2 mb-4">
          {[
            ["analytics", "Analytics"],
            ["editorial", "Editorial Dashboard"],
            ["newsletter", "Newsletter Blast"],
            ["articles", `Scored Articles (${articles.length})`],
            ["blocked", `Blocked from Front Page (${blocked.length})`],
            ["videos", `Scored Videos (${videos.length})`],
            ["subscribers", `Subscribers (${subs.length})`],
            ["sponsors", `Sponsor Inquiries (${sponsors.length})`],
            ["contact", `Contact Messages (${contactMsgs.length})`],
          ].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`btn ${tab === k ? "primary" : ""}`}
              data-testid={`admin-tab-${k}`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="bg-white border border-[var(--line)] rounded-md overflow-hidden">
          {tab === "articles" && (
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--bg)] text-left">
                <tr>
                  <th className="px-3 py-2 font-bold">Title</th>
                  <th className="px-3 py-2 font-bold">Source · Lean</th>
                  <th className="px-3 py-2 font-bold">Trust</th>
                  <th className="px-3 py-2 font-bold">Ev</th>
                  <th className="px-3 py-2 font-bold">Ba</th>
                  <th className="px-3 py-2 font-bold">Im</th>
                  <th className="px-3 py-2 font-bold">Pop</th>
                  <th className="px-3 py-2 font-bold">Noise</th>
                </tr>
              </thead>
              <tbody>
                {articles.map((a) => (
                  <tr key={a.id} className="border-t border-[var(--line)]" data-testid={`admin-art-${a.id}`}>
                    <td className="px-3 py-2 max-w-[420px]">
                      <div className="font-bold text-[var(--ink)] truncate">{a.rewritten_title || a.title}</div>
                      {a.rewritten_title && a.rewritten_title !== a.title && (
                        <div className="text-[11px] text-[var(--muted)] italic truncate">orig: {a.title}</div>
                      )}
                    </td>
                    <td className="px-3 py-2 text-[var(--muted)] whitespace-nowrap">{a.source} · <span className="font-bold text-[var(--ink)]">{a.lean}</span></td>
                    <td className="px-3 py-2 font-black text-[var(--blue)]">{a.trust}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("evidence", a.evidence)}`}>{a.evidence}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("balance", a.balance)}`}>{a.balance}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("impact", a.impact)}`}>{a.impact}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("popularity", a.popularity)}`}>{a.popularity}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("noise", a.noise)}`}>{a.noise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "blocked" && (
            <div className="p-3 space-y-2">
              {blocked.length === 0 ? (
                <div className="text-[var(--muted)] italic p-4 text-center">No blocked articles right now.</div>
              ) : blocked.map((a) => (
                <div key={a.id} className="border border-[var(--line)] rounded-md p-3 bg-[#fff8f8]" data-testid={`admin-blocked-${a.id}`}>
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--red)]">Blocked · Noise {a.noise}</div>
                  <div className="font-bold mt-1">{a.title}</div>
                  <div className="text-[12px] text-[var(--muted)] mt-1">{a.block_reason || "Low substance / outrage framing"} · {a.source}</div>
                </div>
              ))}
            </div>
          )}

          {tab === "videos" && (
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--bg)] text-left">
                <tr>
                  <th className="px-3 py-2">Title</th>
                  <th className="px-3 py-2">Channel · Lean</th>
                  <th className="px-3 py-2">Trust</th>
                  <th className="px-3 py-2">Ev</th>
                  <th className="px-3 py-2">Noise</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((v) => (
                  <tr key={v.id} className="border-t border-[var(--line)]" data-testid={`admin-vid-${v.id}`}>
                    <td className="px-3 py-2 max-w-[420px] truncate font-bold">{v.title}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{v.channel} · <span className="text-[var(--ink)] font-bold">{v.lean}</span></td>
                    <td className="px-3 py-2 font-black text-[var(--blue)]">{v.trust}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("evidence", v.evidence)}`}>{v.evidence}</td>
                    <td className={`px-3 py-2 font-black ${scoreCls("noise", v.noise)}`}>{v.noise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {tab === "subscribers" && (
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--bg)] text-left">
                <tr>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Channel</th>
                  <th className="px-3 py-2">Phone</th>
                  <th className="px-3 py-2">Topics</th>
                  <th className="px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--line)]" data-testid={`admin-sub-${s.id}`}>
                    <td className="px-3 py-2 font-bold">{s.email}</td>
                    <td className="px-3 py-2">{s.channel}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{s.phone || "—"}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{(s.topics || []).join(", ")}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {subs.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-[var(--muted)] italic py-6">No subscribers yet.</td></tr>
                )}
              </tbody>
            </table>
          )}

          {tab === "sponsors" && (
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--bg)] text-left">
                <tr>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Email</th>
                  <th className="px-3 py-2">Slot</th>
                  <th className="px-3 py-2">Budget</th>
                  <th className="px-3 py-2">When</th>
                </tr>
              </thead>
              <tbody>
                {sponsors.map((s) => (
                  <tr key={s.id} className="border-t border-[var(--line)]" data-testid={`admin-spons-${s.id}`}>
                    <td className="px-3 py-2 font-bold">{s.company}</td>
                    <td className="px-3 py-2">{s.contact_name}</td>
                    <td className="px-3 py-2 text-[var(--blue)]">{s.email}</td>
                    <td className="px-3 py-2">{s.slot}</td>
                    <td className="px-3 py-2">{s.budget || "—"}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{new Date(s.created_at).toLocaleString()}</td>
                  </tr>
                ))}
                {sponsors.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-[var(--muted)] italic py-6">No sponsor inquiries yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
          {tab === "analytics" && analytics && (
            <div className="p-5 space-y-5" data-testid="admin-analytics">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["Total Subscribers", analytics.funnel?.total_subscribers ?? 0, "users"],
                  ["Pro", analytics.funnel?.pro ?? 0, "tier"],
                  ["Lite", analytics.funnel?.lite ?? 0, "tier"],
                  ["Active (30d)", analytics.funnel?.active_in_window ?? 0, "engagement"],
                  ["Articles Read", analytics.engagement?.article_reads ?? 0, "30-day"],
                  ["Brief Opens", analytics.engagement?.brief_opens ?? 0, "30-day"],
                  ["Referrals", analytics.engagement?.referrals ?? 0, "30-day"],
                  ["Revenue (USD)", `$${(analytics.revenue?.revenue_usd ?? 0).toFixed(2)}`, "30-day"],
                  ["Payouts (USD)", `$${(analytics.revenue?.payout_usd ?? 0).toFixed(2)}`, "30-day"],
                  ["Net (USD)", `$${(analytics.revenue?.net_usd ?? 0).toFixed(2)}`, "margin"],
                  ["Articles Total", analytics.content?.articles_total ?? 0, "library"],
                  ["Blocked", analytics.content?.blocked ?? 0, "editorial"],
                ].map(([label, val, sub]) => (
                  <div key={label} className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--muted)]">{label}</div>
                    <div className="text-[22px] font-black tabular-nums text-[var(--ink)] mt-1">{val}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--blue)]">{sub}</div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">By Category</div>
                  {Object.entries(analytics.content?.by_category || {}).slice(0, 20).map(([k, n]) => (
                    <div key={k} className="flex justify-between text-[12.5px] py-0.5"><span>{k}</span><b>{n}</b></div>
                  ))}
                </div>
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">By Kind</div>
                  {Object.entries(analytics.content?.by_kind || {}).map(([k, n]) => (
                    <div key={k} className="flex justify-between text-[12.5px] py-0.5"><span>{k}</span><b>{n}</b></div>
                  ))}
                  <div className="mt-3 pt-3 border-t border-[var(--line)]">
                    <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Inbox</div>
                    <div className="flex justify-between text-[12.5px] py-0.5"><span>Open contact messages</span><b>{analytics.inbox?.contact_open ?? 0}</b></div>
                    <div className="flex justify-between text-[12.5px] py-0.5"><span>Open sponsor inquiries</span><b>{analytics.inbox?.sponsor_open ?? 0}</b></div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {tab === "newsletter" && (
            <NewsletterBlastTab adminKey={key} apiBase={API} />
          )}

          {tab === "editorial" && editorial && (
            <div className="p-5 space-y-5" data-testid="admin-editorial">
              {/* Ingest health */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  ["Articles Total", editorial.ingest?.articles_total, ""],
                  ["Not Blocked", editorial.ingest?.articles_not_blocked, "published"],
                  ["Blocked", editorial.ingest?.articles_blocked, "calm filter"],
                  ["Last 24h", editorial.ingest?.articles_last_24h, "ingest"],
                  ["Last 7d", editorial.ingest?.articles_last_7d, "ingest"],
                  ["Avg Trust", editorial.trust?.avg_trust ?? "—", "score"],
                  ["Across-Aisle Hit", editorial.across_aisle_health?.pct != null ? `${editorial.across_aisle_health.pct}%` : "—", "sample"],
                  ["LLM Budget Left", editorial.ingest?.llm_budget?.global_remaining ?? "—", "calls today"],
                ].map(([label, val, sub]) => (
                  <div key={label} className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                    <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--muted)]">{label}</div>
                    <div className="text-[22px] font-black tabular-nums text-[var(--ink)] mt-1">{val ?? 0}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] font-bold text-[var(--blue)]">{sub}</div>
                  </div>
                ))}
              </div>

              {/* Bias band histogram + Lane distribution */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Bias Band Histogram</div>
                  {[
                    ["Left blocked (&lt; -20)", editorial.bias_bands?.left_blocked_lt_minus20, "#dc2626"],
                    ["Slight left (-20 to -1)", editorial.bias_bands?.slight_left_minus20_to_minus1, "#f97316"],
                    ["Center (0 to 9)", editorial.bias_bands?.center_0_to_9, "#6b7280"],
                    ["Sweet spot (10 to 44)", editorial.bias_bands?.sweet_spot_10_to_44, "#15803d"],
                    ["Stronger right (45-74)", editorial.bias_bands?.stronger_right_45_to_74, "#0a4d3a"],
                    ["Right blocked (≥ 75)", editorial.bias_bands?.right_blocked_gte_75, "#dc2626"],
                  ].map(([label, n, color]) => (
                    <div key={label} className="flex items-center gap-2 text-[12.5px] py-1">
                      <span className="w-2 h-2 rounded-full shrink-0" style={{ background: color }} />
                      <span className="flex-1" dangerouslySetInnerHTML={{ __html: label }} />
                      <b className="tabular-nums">{n || 0}</b>
                    </div>
                  ))}
                </div>
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Lane Distribution (Internal)</div>
                  {Object.entries(editorial.lanes || {}).filter(([, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                    <div key={k} className="flex justify-between text-[12.5px] py-0.5"><span>{k}</span><b className="tabular-nums">{n}</b></div>
                  ))}
                </div>
              </div>

              {/* Top sources by trust + Block reasons */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Top Sources by Trust (7d, ≥3 articles)</div>
                  {(editorial.top_sources_7d || []).map((s) => (
                    <div key={s.source} className="flex items-center gap-2 text-[12.5px] py-0.5">
                      <span className="flex-1 truncate">{s.source}</span>
                      <span className="text-[var(--muted)] tabular-nums">×{s.articles}</span>
                      <b className="tabular-nums text-[var(--blue)] min-w-[36px] text-right">{s.avg_trust}</b>
                    </div>
                  ))}
                  {(editorial.top_sources_7d || []).length === 0 && <div className="text-[12px] text-[var(--muted)] italic py-2">No sources scored yet in 7d.</div>}
                </div>
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Top Block Reasons</div>
                  {Object.entries(editorial.block_reasons || {}).slice(0, 12).map(([k, n]) => (
                    <div key={k} className="flex justify-between text-[12.5px] py-0.5">
                      <span className="flex-1 truncate text-[var(--muted)]">{k}</span>
                      <b className="tabular-nums">{n}</b>
                    </div>
                  ))}
                </div>
              </div>

              {/* Calm pillar + 24h categories + Revenue */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Calm Pillar Health</div>
                  {Object.entries(editorial.calm_pillar || {}).map(([field, stats]) => (
                    <div key={field} className="text-[12.5px] py-1 border-b border-[var(--line)] last:border-0">
                      <div className="font-bold">{field.replace("_score", "")}</div>
                      <div className="flex justify-between text-[var(--muted)]">
                        <span>{stats.passing} passing / {stats.scored} scored</span>
                        <b className="text-[var(--blue)]">{stats.pct_passing != null ? `${stats.pct_passing}%` : "—"}</b>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Categories (24h ingest)</div>
                  {Object.entries(editorial.categories_24h || {}).map(([k, n]) => (
                    <div key={k} className="flex justify-between text-[12.5px] py-0.5"><span>{k}</span><b className="tabular-nums">{n}</b></div>
                  ))}
                </div>
                <div className="bg-[var(--bg)] border border-[var(--line)] rounded-md p-3">
                  <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)] mb-2">Revenue Indicators</div>
                  <div className="flex justify-between text-[12.5px] py-0.5"><span>Subscribers total</span><b className="tabular-nums">{editorial.revenue?.subscribers_total ?? 0}</b></div>
                  <div className="flex justify-between text-[12.5px] py-0.5"><span>Newsletter subs</span><b className="tabular-nums">{editorial.revenue?.newsletter_subs ?? 0}</b></div>
                  <div className="flex justify-between text-[12.5px] py-0.5"><span>Sponsor inquiries</span><b className="tabular-nums">{editorial.revenue?.sponsor_inquiries_total ?? 0}</b></div>
                  <div className="flex justify-between text-[12.5px] py-0.5"><span>Sponsor inquiries 7d</span><b className="tabular-nums">{editorial.revenue?.sponsor_inquiries_last_7d ?? 0}</b></div>
                  <div className="flex justify-between text-[12.5px] py-0.5"><span>Sellpages listings</span><b className="tabular-nums">{editorial.revenue?.sellpages_listings ?? 0}</b></div>
                  <div className="mt-2 pt-2 border-t border-[var(--line)]">
                    <div className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--muted)] mb-1">Paid tiers</div>
                    {Object.entries(editorial.paid_tiers || {}).filter(([, n]) => n > 0).map(([k, n]) => (
                      <div key={k} className="flex justify-between text-[12px] py-0.5"><span>{k}</span><b className="tabular-nums">{n}</b></div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="text-[11px] text-[var(--muted)] italic pt-2">
                Generated at {editorial.generated_at ? new Date(editorial.generated_at).toLocaleString() : "—"} · OPERATOR-ONLY · do not share lane data publicly
              </div>
            </div>
          )}

          {tab === "contact" && (
            <table className="w-full text-[13px]">
              <thead className="bg-[var(--bg)] text-left">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">From</th>
                  <th className="px-3 py-2">Intent</th>
                  <th className="px-3 py-2">Subject</th>
                  <th className="px-3 py-2">Message</th>
                  <th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {contactMsgs.map((m) => (
                  <tr key={m.id} className="border-t border-[var(--line)]" data-testid={`admin-contact-${m.id}`}>
                    <td className="px-3 py-2 text-[var(--muted)] whitespace-nowrap">{new Date(m.created_at).toLocaleString()}</td>
                    <td className="px-3 py-2"><div className="font-bold">{m.name}</div><div className="text-[var(--blue)] text-[11px]">{m.email}</div></td>
                    <td className="px-3 py-2"><span className="chip">{m.intent}</span></td>
                    <td className="px-3 py-2 font-semibold">{m.subject}</td>
                    <td className="px-3 py-2 text-[var(--muted)]">{(m.message || "").slice(0, 200)}{m.message?.length > 200 ? "…" : ""}</td>
                    <td className="px-3 py-2"><span className={`chip ${m.status === "resolved" ? "" : "!bg-[var(--gold)] !text-[#1d1303] !border-[var(--gold)]"}`}>{m.status}</span></td>
                  </tr>
                ))}
                {contactMsgs.length === 0 && (
                  <tr><td colSpan={6} className="text-center text-[var(--muted)] italic py-6">No contact messages yet.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </section>

      <section className="max-w-[1380px] mx-auto px-5 py-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-[13px]">
        <div className="bg-white border border-[var(--line)] rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">Composite Trust Score</div>
          <p className="mt-2 text-[var(--muted)] leading-relaxed">
            <code>trust = clamp(0.35·evidence + 0.25·impact + 0.20·balance + 0.10·popularity − 0.40·noise + 30)</code>
          </p>
        </div>
        <div className="bg-white border border-[var(--line)] rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">Block Rules</div>
          <p className="mt-2 text-[var(--muted)] leading-relaxed">
            Front-page block if LLM flags blocked OR noise &gt; 70. Filtered automatically from public feed.
          </p>
        </div>
        <div className="bg-white border border-[var(--line)] rounded-md p-4">
          <div className="text-[11px] uppercase tracking-[0.14em] font-black text-[var(--blue)]">Source Mix</div>
          <p className="mt-2 text-[var(--muted)] leading-relaxed">
            16 article feeds + 11 YouTube channels, lean-tagged: center, center-left, center-right, right, primary.
          </p>
        </div>
      </section>
    </main>
  );
}

// Newsletter Blast tab — admin composes HTML and sends to subscribers.
function NewsletterBlastTab({ adminKey, apiBase }) {
  const [subject, setSubject] = useState("");
  const [audience, setAudience] = useState("all");
  const [html, setHtml] = useState("<h2>This week on Newseze</h2>\n<p>Hello —</p>\n<p>Here's what we've been covering this week:</p>\n<ul><li><a href=\"https://newseze.com\">Top stories</a></li></ul>\n<p>— The Newseze Team</p>");
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const send = async (e) => {
    e.preventDefault();
    if (!confirm(`Send to "${audience}" audience? This action cannot be undone.`)) return;
    setSending(true); setErr(""); setResult(null);
    try {
      const r = await axios.post(`${apiBase}/admin/newsletter-blast`,
        { subject, html_body: html, audience },
        { headers: { "X-Admin-Key": adminKey } });
      setResult(r.data);
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Send failed.");
    } finally { setSending(false); }
  };

  return (
    <div className="p-5" data-testid="admin-newsletter-tab">
      <div className="text-[16px] font-black mb-3">Compose & send newsletter</div>
      <p className="text-[12.5px] text-[var(--muted)] mb-4">
        Sends an HTML newsletter via Resend. An unsubscribe link is appended automatically.
        Capped at 1000 sends per call.
      </p>
      <form onSubmit={send} className="space-y-4 max-w-[820px]">
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">Subject line</span>
          <input required value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="newsletter-subject" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">Audience</span>
          <select value={audience} onChange={(e) => setAudience(e.target.value)} className="w-full mt-1 border border-[var(--line)] rounded-md p-2 text-[13px]" data-testid="newsletter-audience">
            <option value="all">All subscribers</option>
            <option value="newsletter">Newsletter sign-ups only</option>
            <option value="tier_basic">Reader tier ($4.99)</option>
            <option value="tier_pro">Pro tier ($9.99)</option>
            <option value="tier_channels">Channel Builder tier ($19.99)</option>
            <option value="tier_elite">Elite tier ($49.99)</option>
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-[0.16em] font-black text-[var(--ink)]">HTML body</span>
          <textarea required value={html} onChange={(e) => setHtml(e.target.value)} rows={14} className="w-full mt-1 border border-[var(--line)] rounded-md p-3 text-[12.5px] font-mono" data-testid="newsletter-html" />
        </label>
        <div className="rounded-md border border-[var(--line)] bg-[var(--bg)] p-3">
          <div className="text-[10px] uppercase tracking-[0.14em] font-black text-[var(--muted)] mb-1">Preview</div>
          <div className="prose prose-sm max-w-none text-[13px]" dangerouslySetInnerHTML={{ __html: html }} data-testid="newsletter-preview" />
        </div>
        {err && <div className="text-[12.5px] text-rose-700">{err}</div>}
        {result && (
          <div className="text-[12.5px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-3" data-testid="newsletter-result">
            Sent <b>{result.sent}</b> of {result.recipients_total} ({result.failures || 0} failures) to audience <b>{result.audience}</b>.
          </div>
        )}
        <button type="submit" disabled={sending || !subject || !html} className="btn primary" data-testid="newsletter-send">
          {sending ? "Sending…" : "Send newsletter"}
        </button>
      </form>
    </div>
  );
}
